import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getAuthHeaders } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Upload, X, Plus, ArrowLeft, Search, ChevronDown, ChevronUp, GripVertical, Trash2, CheckSquare, Square } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  title: "", slug: "", short_description: "", full_description: "",
  bedrooms: 0, bathrooms: 0, square_feet: 0, beds: 0, sleeps: 0,
  location: "", amenities: [], house_rules: "",
  airbnb_url: "", vrbo_url: "",
  featured_image: "", gallery_images: [],
  is_featured: false, is_published: false, sort_order: 0,
  meta_title: "", meta_description: ""
};

export default function AdminListingForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [newAmenity, setNewAmenity] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [amenitiesCategories, setAmenitiesCategories] = useState({});
  const [amenitySearch, setAmenitySearch] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedGallery, setSelectedGallery] = useState(new Set());
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  useEffect(() => {
    if (isEdit) {
      axios.get(`${API}/admin/listings`, { headers: getAuthHeaders(), withCredentials: true })
        .then(res => {
          const listing = res.data.find(l => l.id === id);
          if (listing) {
            setForm({
              title: listing.title || "", slug: listing.slug || "",
              short_description: listing.short_description || "", full_description: listing.full_description || "",
              bedrooms: listing.bedrooms || 0, bathrooms: listing.bathrooms || 0,
              square_feet: listing.square_feet || 0, beds: listing.beds || 0, sleeps: listing.sleeps || 0,
              location: listing.location || "", amenities: listing.amenities || [],
              house_rules: listing.house_rules || "", airbnb_url: listing.airbnb_url || "",
              vrbo_url: listing.vrbo_url || "", featured_image: listing.featured_image || "",
              gallery_images: listing.gallery_images || [], is_featured: listing.is_featured || false,
              is_published: listing.is_published || false, sort_order: listing.sort_order || 0,
              meta_title: listing.meta_title || "", meta_description: listing.meta_description || "",
            });
          }
        })
        .catch(() => toast.error("Failed to load listing"))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  useEffect(() => {
    axios.get(`${API}/amenities`).then(res => {
      setAmenitiesCategories(res.data);
      const expanded = {};
      Object.keys(res.data).forEach(k => { expanded[k] = true; });
      setExpandedCategories(expanded);
    }).catch(() => {});
  }, []);

  const toggleAmenity = (amenity) => {
    setForm(f => ({
      ...f,
      amenities: f.amenities.includes(amenity)
        ? f.amenities.filter(a => a !== amenity)
        : [...f.amenities, amenity]
    }));
  };

  const toggleCategory = (cat) => {
    setExpandedCategories(e => ({ ...e, [cat]: !e[cat] }));
  };

  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (field === "title" && !isEdit) {
      const slug = value.toLowerCase().replace(/[^\w\s-]/g, "").replace(/[\s_]+/g, "-").replace(/-+/g, "-").trim();
      setForm(f => ({ ...f, slug }));
    }
  };

  const addAmenity = () => {
    if (newAmenity.trim() && !form.amenities.includes(newAmenity.trim())) {
      setForm(f => ({ ...f, amenities: [...f.amenities, newAmenity.trim()] }));
      setNewAmenity("");
    }
  };

  const removeAmenity = (idx) => {
    setForm(f => ({ ...f, amenities: f.amenities.filter((_, i) => i !== idx) }));
  };

  const uploadImage = async (file) => {
    // Client-side validation
    const maxSize = 50 * 1024 * 1024;
    const minSize = 1024;
    if (file.size > maxSize) {
      setUploadError(`File "${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(1)} MB).\n\nMaximum file size: 50 MB\n\nPlease reduce your image size or compress it before uploading.`);
      return null;
    }
    if (file.size < minSize) {
      setUploadError(`File "${file.name}" is too small (${file.size} bytes).\n\nMinimum file size: 1 KB\n\nThe file may be corrupt or empty.`);
      return null;
    }
    if (!file.type.startsWith("image/")) {
      setUploadError(`File "${file.name}" is not a valid image (type: ${file.type || 'unknown'}).\n\nAllowed formats: JPG, PNG, WebP, GIF, BMP, TIFF, HEIC, AVIF\n\nPlease select an image file.`);
      return null;
    }

    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/upload/image`, formData, {
        headers: getAuthHeaders()
      });
      return `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Upload failed. Please try a different image format (JPG or PNG) or a smaller file size.";
      setUploadError(`Upload failed for "${file.name}"\n\n${msg}\n\nImage requirements:\n• Formats: JPG, PNG, WebP, GIF\n• Max size: 50 MB\n• Min size: 1 KB`);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleFeaturedUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file);
    if (url) setForm(f => ({ ...f, featured_image: url }));
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      const url = await uploadImage(file);
      if (url) setForm(f => ({ ...f, gallery_images: [...f.gallery_images, url] }));
    }
  };

  const removeGalleryImage = (idx) => {
    setForm(f => ({ ...f, gallery_images: f.gallery_images.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/listings/${id}`, form, { headers: getAuthHeaders(), withCredentials: true });
        toast.success("Listing updated");
      } else {
        await axios.post(`${API}/admin/listings`, form, { headers: getAuthHeaders(), withCredentials: true });
        toast.success("Listing created");
      }
      navigate("/admin/listings");
    } catch {
      toast.error("Failed to save listing");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center py-24">
      <div className="w-8 h-8 border-2 border-[#32A334] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const inputClass = "bg-white border border-[#E2E8F0] focus:border-[#32A334] text-[#1A1A2E] py-3 px-4 outline-none w-full text-sm transition-colors rounded";

  return (
    <div data-testid="admin-listing-form" className="px-6 md:px-12 lg:px-24 py-8 max-w-5xl">
      {/* Upload Error Dialog */}
      {uploadError && (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center" data-testid="upload-error-modal">
          <div className="bg-white border border-[#E2E8F0] p-8 max-w-md w-full mx-4 rounded-lg shadow-lg">
            <h3 className="font-['DM_Serif_Display'] text-xl text-[#D94E4E] mb-4">Upload Error</h3>
            <p className="text-[#1A1A2E] text-sm leading-relaxed whitespace-pre-line mb-6">{uploadError}</p>
            <button
              onClick={() => setUploadError(null)}
              data-testid="upload-error-ok"
              className="bg-[#1C671D] text-white hover:bg-[#145215] transition-colors duration-300 px-8 py-2.5 uppercase tracking-widest text-sm font-semibold rounded w-full"
            >
              OK
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => navigate("/admin/listings")}
        className="flex items-center gap-2 text-[#64748B] hover:text-[#1A1A2E] text-sm mb-8 transition-colors"
        data-testid="back-to-listings"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Listings
      </button>

      <h1 className="font-['DM_Serif_Display'] text-3xl sm:text-4xl text-[#1A1A2E] mb-8">
        {isEdit ? "Edit Listing" : "New Listing"}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-10">
        {/* Basic Info */}
        <section className="space-y-6">
          <h2 className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] pb-2 border-b border-[#E2E8F0]">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#64748B] mb-2 block">Title *</label>
              <input value={form.title} onChange={(e) => handleChange("title", e.target.value)} data-testid="form-title" className={inputClass} required />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#64748B] mb-2 block">Slug</label>
              <input value={form.slug} onChange={(e) => handleChange("slug", e.target.value)} data-testid="form-slug" className={inputClass} />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-[#64748B] mb-2 block">Location</label>
            <input value={form.location} onChange={(e) => handleChange("location", e.target.value)} data-testid="form-location" className={inputClass} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-[#64748B] mb-2 block">Short Description</label>
            <textarea value={form.short_description} onChange={(e) => handleChange("short_description", e.target.value)} rows={2} data-testid="form-short-desc" className={`${inputClass} resize-none`} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-[#64748B] mb-2 block">Full Description</label>
            <textarea value={form.full_description} onChange={(e) => handleChange("full_description", e.target.value)} rows={6} data-testid="form-full-desc" className={`${inputClass} resize-none`} />
          </div>
        </section>

        {/* Numbers */}
        <section className="space-y-6">
          <h2 className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] pb-2 border-b border-[#E2E8F0]">Property Details</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {[
              { field: "bedrooms", label: "Bedrooms", type: "number" },
              { field: "bathrooms", label: "Bathrooms", type: "number", step: "0.5" },
              { field: "square_feet", label: "Sq Ft", type: "number" },
              { field: "beds", label: "Beds", type: "number" },
              { field: "sleeps", label: "Sleeps", type: "number" },
            ].map(({ field, label, type, step }) => (
              <div key={field}>
                <label className="text-xs uppercase tracking-[0.15em] text-[#64748B] mb-2 block">{label}</label>
                <input type={type} step={step} value={form[field]} onChange={(e) => handleChange(field, parseFloat(e.target.value) || 0)} data-testid={`form-${field}`} className={inputClass} />
              </div>
            ))}
          </div>
        </section>

        {/* Images */}
        <section className="space-y-6">
          <h2 className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] pb-2 border-b border-[#E2E8F0]">Media</h2>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-[#64748B] mb-3 block">Featured Image</label>
            {form.featured_image ? (
              <div className="relative w-48 h-32 overflow-hidden bg-[#F4F6FA] group rounded">
                <img src={form.featured_image} alt="Featured" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setForm(f => ({ ...f, featured_image: "" }))} className="absolute top-1 right-1 bg-black/60 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity rounded">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <label className="cursor-pointer inline-flex items-center gap-2 border border-dashed border-[#CBD5E1] hover:border-[#32A334] text-[#64748B] px-6 py-3 text-xs uppercase tracking-[0.15em] transition-colors rounded" data-testid="upload-featured">
                <Upload className="w-4 h-4" /> Upload Image
                <input type="file" accept="image/*" onChange={handleFeaturedUpload} className="hidden" />
              </label>
            )}
            <div className="mt-2">
              <label className="text-xs text-[#94A3B8]">Or paste URL:</label>
              <input value={form.featured_image} onChange={(e) => handleChange("featured_image", e.target.value)} data-testid="form-featured-url" placeholder="https://..." className="bg-white border border-[#E2E8F0] focus:border-[#32A334] text-[#1A1A2E] py-2 px-3 outline-none w-full text-xs transition-colors rounded mt-1" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs uppercase tracking-[0.15em] text-[#64748B]">Gallery Images ({form.gallery_images.length})</label>
              {form.gallery_images.length > 0 && (
                <div className="flex items-center gap-3">
                  {selectedGallery.size > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setForm(f => ({ ...f, gallery_images: f.gallery_images.filter((_, i) => !selectedGallery.has(i)) }));
                        setSelectedGallery(new Set());
                      }}
                      data-testid="delete-selected-gallery"
                      className="text-[#D94E4E] text-xs uppercase tracking-[0.1em] flex items-center gap-1 hover:text-red-700 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete Selected ({selectedGallery.size})
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (selectedGallery.size === form.gallery_images.length) {
                        setSelectedGallery(new Set());
                      } else {
                        setSelectedGallery(new Set(form.gallery_images.map((_, i) => i)));
                      }
                    }}
                    data-testid="select-all-gallery"
                    className="text-[#64748B] text-xs uppercase tracking-[0.1em] flex items-center gap-1 hover:text-[#1A1A2E] transition-colors"
                  >
                    {selectedGallery.size === form.gallery_images.length ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                    {selectedGallery.size === form.gallery_images.length ? "Deselect All" : "Select All"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setForm(f => ({ ...f, gallery_images: [] })); setSelectedGallery(new Set()); }}
                    data-testid="delete-all-gallery"
                    className="text-[#D94E4E] text-xs uppercase tracking-[0.1em] flex items-center gap-1 hover:text-red-700 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete All
                  </button>
                </div>
              )}
            </div>
            <p className="text-xs text-[#94A3B8] mb-3">Drag images to reorder. Click checkbox to select.</p>
            <div className="flex flex-wrap gap-3 mb-3" data-testid="gallery-images-grid">
              {form.gallery_images.map((img, idx) => {
                const isSelected = selectedGallery.has(idx);
                return (
                  <div
                    key={`${idx}-${img.slice(-20)}`}
                    draggable
                    onDragStart={(e) => {
                      dragItem.current = idx;
                      e.dataTransfer.effectAllowed = 'move';
                      e.currentTarget.style.opacity = '0.4';
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.style.opacity = '1';
                      if (dragItem.current !== null && dragOverItem.current !== null && dragItem.current !== dragOverItem.current) {
                        const from = dragItem.current;
                        const to = dragOverItem.current;
                        setForm(f => {
                          const imgs = [...f.gallery_images];
                          const dragged = imgs.splice(from, 1)[0];
                          imgs.splice(to, 0, dragged);
                          return { ...f, gallery_images: imgs };
                        });
                        setSelectedGallery(new Set());
                      }
                      dragItem.current = null;
                      dragOverItem.current = null;
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      dragOverItem.current = idx;
                      e.currentTarget.style.borderColor = '#1C671D';
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.style.borderColor = isSelected ? '#1C671D' : 'transparent';
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.style.borderColor = isSelected ? '#1C671D' : 'transparent';
                    }}
                    className={`relative w-28 h-28 overflow-hidden bg-[#F4F6FA] group rounded cursor-grab active:cursor-grabbing border-2 transition-colors ${
                      isSelected ? 'border-[#1C671D]' : 'border-transparent hover:border-[#CBD5E1]'
                    }`}
                    data-testid={`gallery-item-${idx}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover pointer-events-none" />
                    {/* Drag handle */}
                    <div className="absolute top-1 left-1 bg-black/50 text-white p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      <GripVertical className="w-3.5 h-3.5" />
                    </div>
                    {/* Checkbox */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedGallery(prev => {
                          const next = new Set(prev);
                          if (next.has(idx)) next.delete(idx); else next.add(idx);
                          return next;
                        });
                      }}
                      className={`absolute top-1 right-1 p-0.5 rounded transition-opacity ${
                        isSelected ? 'opacity-100 bg-[#1C671D] text-white' : 'opacity-0 group-hover:opacity-100 bg-black/50 text-white'
                      }`}
                      data-testid={`gallery-select-${idx}`}
                    >
                      {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                    </button>
                    {/* Order number */}
                    <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                      {idx + 1}
                    </div>
                  </div>
                );
              })}
              <label className="cursor-pointer w-28 h-28 border border-dashed border-[#CBD5E1] hover:border-[#32A334] flex flex-col items-center justify-center text-[#94A3B8] transition-colors rounded gap-1" data-testid="upload-gallery">
                <Plus className="w-6 h-6" />
                <span className="text-[10px]">Add Images</span>
                <input type="file" accept="image/*" multiple onChange={handleGalleryUpload} className="hidden" />
              </label>
            </div>
            {uploading && <p className="text-[#32A334] text-xs">Uploading...</p>}
          </div>
        </section>

        {/* Amenities */}
        <section className="space-y-6">
          <h2 className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] pb-2 border-b border-[#E2E8F0]">Amenities</h2>

          {/* Selected count */}
          <p className="text-sm text-[#64748B]">{form.amenities.length} amenities selected</p>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <input
              value={amenitySearch}
              onChange={(e) => setAmenitySearch(e.target.value)}
              placeholder="Search amenities..."
              data-testid="amenity-search"
              className={`${inputClass} pl-10`}
            />
          </div>

          {/* Categories */}
          <div className="space-y-2 max-h-[500px] overflow-y-auto border border-[#E2E8F0] rounded p-1" data-testid="amenities-checklist">
            {Object.entries(amenitiesCategories).map(([category, items]) => {
              const filtered = amenitySearch
                ? items.filter(a => a.toLowerCase().includes(amenitySearch.toLowerCase()))
                : items;
              if (filtered.length === 0) return null;
              const selectedInCat = filtered.filter(a => form.amenities.includes(a)).length;
              const isOpen = expandedCategories[category];

              return (
                <div key={category} className="border-b border-[#E2E8F0] last:border-b-0">
                  <button
                    type="button"
                    onClick={() => toggleCategory(category)}
                    className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-[#F4F6FA] transition-colors"
                    data-testid={`amenity-cat-${category.replace(/\s/g, '-').toLowerCase()}`}
                  >
                    <span className="text-sm font-medium text-[#1A1A2E]">
                      {category}
                      {selectedInCat > 0 && (
                        <span className="ml-2 text-xs text-[#32A334] font-normal">({selectedInCat} selected)</span>
                      )}
                    </span>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-[#94A3B8]" /> : <ChevronDown className="w-4 h-4 text-[#94A3B8]" />}
                  </button>
                  {isOpen && (
                    <div className="px-3 pb-3 grid grid-cols-2 md:grid-cols-3 gap-1">
                      {filtered.map(amenity => (
                        <label
                          key={amenity}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm transition-colors ${
                            form.amenities.includes(amenity) ? 'bg-[#1C671D]/10 text-[#1C671D]' : 'text-[#64748B] hover:bg-[#F4F6FA]'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={form.amenities.includes(amenity)}
                            onChange={() => toggleAmenity(amenity)}
                            className="accent-[#1C671D] w-3.5 h-3.5 shrink-0"
                          />
                          <span className="text-xs">{amenity}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Custom amenity */}
          <div className="flex gap-3">
            <input
              value={newAmenity}
              onChange={(e) => setNewAmenity(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addAmenity())}
              placeholder="Add custom amenity..."
              data-testid="form-amenity-input"
              className={`${inputClass} flex-1`}
            />
            <button type="button" onClick={addAmenity} data-testid="add-amenity-btn" className="bg-[#1C671D] text-white hover:bg-[#145215] transition-colors text-xs uppercase tracking-[0.15em] px-5 py-2 rounded">
              Add
            </button>
          </div>

          {/* Selected tags */}
          {form.amenities.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {form.amenities.map((a, i) => (
                <span key={i} className="flex items-center gap-1.5 px-3 py-1 bg-[#1C671D]/10 text-[#1C671D] text-xs rounded border border-[#1C671D]/20">
                  {a}
                  <button type="button" onClick={() => toggleAmenity(a)} className="text-[#1C671D]/60 hover:text-[#D94E4E]"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
        </section>

        {/* House Rules */}
        <section className="space-y-6">
          <h2 className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] pb-2 border-b border-[#E2E8F0]">House Rules</h2>
          <textarea value={form.house_rules} onChange={(e) => handleChange("house_rules", e.target.value)} rows={4} data-testid="form-house-rules" className={`${inputClass} resize-none`} />
        </section>

        {/* Links */}
        <section className="space-y-6">
          <h2 className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] pb-2 border-b border-[#E2E8F0]">Booking Links</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#64748B] mb-2 block">Airbnb URL</label>
              <input value={form.airbnb_url} onChange={(e) => handleChange("airbnb_url", e.target.value)} data-testid="form-airbnb-url" className={inputClass} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#64748B] mb-2 block">VRBO URL</label>
              <input value={form.vrbo_url} onChange={(e) => handleChange("vrbo_url", e.target.value)} data-testid="form-vrbo-url" className={inputClass} />
            </div>
          </div>
        </section>

        {/* Toggles */}
        <section className="space-y-6">
          <h2 className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] pb-2 border-b border-[#E2E8F0]">Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <label className="flex items-center gap-3 cursor-pointer" data-testid="form-published-toggle">
              <input type="checkbox" checked={form.is_published} onChange={(e) => handleChange("is_published", e.target.checked)} className="accent-[#32A334] w-4 h-4" />
              <span className="text-[#1A1A2E] text-sm">Published</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer" data-testid="form-featured-toggle">
              <input type="checkbox" checked={form.is_featured} onChange={(e) => handleChange("is_featured", e.target.checked)} className="accent-[#32A334] w-4 h-4" />
              <span className="text-[#1A1A2E] text-sm">Featured</span>
            </label>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#64748B] mb-2 block">Sort Order</label>
              <input type="number" value={form.sort_order} onChange={(e) => handleChange("sort_order", parseInt(e.target.value) || 0)} data-testid="form-sort-order" className={inputClass} />
            </div>
          </div>
        </section>

        {/* SEO */}
        <section className="space-y-6">
          <h2 className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] pb-2 border-b border-[#E2E8F0]">SEO</h2>
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#64748B] mb-2 block">Meta Title</label>
              <input value={form.meta_title} onChange={(e) => handleChange("meta_title", e.target.value)} data-testid="form-meta-title" className={inputClass} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#64748B] mb-2 block">Meta Description</label>
              <textarea value={form.meta_description} onChange={(e) => handleChange("meta_description", e.target.value)} rows={2} data-testid="form-meta-desc" className={`${inputClass} resize-none`} />
            </div>
          </div>
        </section>

        {/* Submit */}
        <div className="flex gap-4 pt-4">
          <button type="submit" disabled={saving} data-testid="save-listing-btn" className="bg-[#1C671D] text-white hover:bg-[#145215] transition-colors duration-300 px-8 py-3.5 uppercase tracking-widest text-sm font-semibold disabled:opacity-50 rounded">
            {saving ? "Saving..." : (isEdit ? "Update Listing" : "Create Listing")}
          </button>
          <button type="button" onClick={() => navigate("/admin/listings")} className="border border-[#E2E8F0] text-[#1A1A2E] hover:bg-[#F4F6FA] transition-colors px-8 py-3.5 text-sm uppercase tracking-widest rounded">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
